import React from 'react';
import { Slider } from '@ark-ui/react/slider';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface SliderInputProps extends BaseInputProps<number> {
  min?: number;
  max?: number;
  step?: number;
  /** Zobrazit aktuální hodnotu vedle popisku. */
  showValue?: boolean;
  className?: string;
}

/**
 * Atomický posuvník (jeden thumb) postavený na Ark UI `Slider`.
 *
 * RHF drží jednu `number` hodnotu; Ark pracuje s polem hodnot (více thumbů),
 * proto mapujeme `value` ↔ `[value]`. `ids.label` míří na popisek
 * z `<FormField />`, ze kterého zag odvodí `aria-labelledby` thumbu — jinak
 * by posuvník neměl pro AT žádné jméno.
 */
export const SliderInput: React.FC<SliderInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  min = 0,
  max = 100,
  step = 1,
  showValue = true,
  className,
}) => {
  const sliderValue = React.useMemo(() => [value ?? min], [value, min]);
  const ids = React.useMemo(
    () => ({
      label: labelId,
      hiddenInput: (index: number) => (index === 0 ? id : `${id}-${index}`),
    }),
    [labelId, id],
  );

  return (
    <Slider.Root
      name={name}
      value={sliderValue}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      ids={ids}
      onValueChange={(details) => onChange(details.value[0] ?? min)}
      onBlur={onBlur}
      className={clsx(styles.sliderRoot, className)}
      {...testAttributes}
    >
      {showValue && (
        <Slider.ValueText className={styles.sliderValueText} />
      )}
      <Slider.Control className={styles.sliderControl}>
        <Slider.Track className={styles.sliderTrack}>
          <Slider.Range className={styles.sliderRange} />
        </Slider.Track>
        <Slider.Thumb
          index={0}
          ref={ref}
          className={styles.sliderThumb}
          aria-describedby={describedBy}
        >
          <Slider.HiddenInput />
        </Slider.Thumb>
      </Slider.Control>
    </Slider.Root>
  );
};

SliderInput.displayName = 'SliderInput';
